import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { NodeEdgeMapper } from '@iotv/datamodel-core';
import { isGeoGrid } from '@iotv/iotv-v3-types';
import { Alert, Grid, Skeleton, Stack } from '@mui/material';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { getNodeEdgeMapperReducer } from '../../../ContextProviders/NodeEdgeMapperComponentContext';
import { GoogleMapAPIMapTypeIds } from '../../../GoogleMapV4';
import { AreaUpdateableLabelFn } from '../../../GoogleMapV4/components/Labels/AreaUpdateablePortalLabel';
import { NullLabel } from '../../../GoogleMapV4/components/Labels/NullLabel';
import { useThemedMap } from '../../../GoogleMapV4/useThemedMap';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { getAreaViewDefinition } from '../viewDefinitions';
const debug = process.env.REACT_APP_DEBUG && false;
/** shimmed for NetworkCoverage */
const areaData = [];
const lineDefiningPairs = [];
const nemReducer = getNodeEdgeMapperReducer({ canaryString: 'areaDetailMapReducer' });
export const AreaDetailCard = ({ contextArea, transactionFunctions, userFunctions, allowedActions, isSmallView, applicationData }) => {
    const viewDefinition = getAreaViewDefinition();
    const [{ nodeEdgeMapper: mapNem, nemHash }, mapDispatch] = useReducer(nemReducer, {
        nodeEdgeMapper: new NodeEdgeMapper(),
        nemHash: null,
        apexNode: null,
    });
    const AreaLabelFnRef = useRef(
    //({ vob }) => ((vob as Partial<GeneratedGeoObject>).metadata?.generatedFrom ? <NullLabel {...{ vob }} /> : <DefaultPortalLabel {...{ vob }} />),
    ({ vob }) => vob.metadata?.generatedFrom ? (_jsx(NullLabel, { vob })) : isGeoGrid(vob) ? (_jsx(_Fragment, {})) : (_jsx(AreaUpdateableLabelFn, { vob, contextArea })));
    const themedMapInput = useMemo(() => {
        //const getUseThemedMapInput = useCallback<() => UseThemedMapInput>(() => {
        debug && console.log(`AreaDetailCard useThemedMapInput callback`, nemHash);
        return {
            canaryString: 'AreaDetailCard',
            contextObject: contextArea,
            nodeEdgeMapper: mapNem,
            areaData,
            lineDefiningPairs,
            nodeEdgeMapperDispatch: mapDispatch,
            transactionFunctions,
            options: {
                tilt: 0,
                mapTypeId: GoogleMapAPIMapTypeIds.terrain,
                mapTypeControlOptions: {
                    position: google.maps.ControlPosition.BOTTOM_RIGHT,
                },
                controlSize: 24,
            },
            dataFn: (async (gob) => {
                return { ...gob, data: {} };
            }),
            functions: {},
            showDrawingTools: true,
            themesAndLabels: {
                Area: {
                    LabelComponent: AreaLabelFnRef.current,
                },
            },
        };
    }, [mapNem, contextArea.sk, nemHash]);
    const { map, components: { PortalDiv, MapDatalayerVisibility, MapLegend }, quarantinedItemsDialog, mapActionsDialog, } = useThemedMap(themedMapInput);
    useEffect(() => {
        if (!applicationData)
            return;
        debug && console.log('New context area');
        // Clear the map incase we're moving from thingview/area to thingview/area. It aint pretty (flashes the quarantined items dialog), but it works
        // TODO: if you move an area, save on map, then click that area, it moves back, because the area hasn't been updated in applicationData (even though I told it to???)
        // Cos thingview only loads from appdata at container level!!!
        mapDispatch({ type: 'clearNEMContents', value: null });
        // Get child areas and places from state, which I have because areaview loads them into appstate
        const childItems = applicationData.getDecendents([contextArea]).filter((n) => n.type === 'Area' || n.type === 'Place');
        mapDispatch({ type: 'loadItems', value: { items: [contextArea, ...childItems] } });
    }, [contextArea.sk, applicationData]);
    const direction = isSmallView ? 'column' : 'row';
    const controlsMargin = 12;
    const controlsWidth = isSmallView ? '250px' : `calc(100% - ${2 * controlsMargin}px)`;
    const justifyContent = isSmallView ? undefined : 'flex-end';
    const spacing = isSmallView ? 0 : 1;
    const controlsStack = (_jsxs(Stack, { width: controlsWidth, spacing, direction, sx: { zIndex: 2, height: 0, position: 'relative', top: controlsMargin, left: controlsMargin }, children: [_jsxs(Stack, { direction, spacing, width: isSmallView ? undefined : '75%', children: [_jsx(Stack, { id: 'visibility', children: MapDatalayerVisibility }, 'nwcmMapDatalayerVisibility'), _jsx(Stack, { id: 'mapControlModalBase' }, 'EC')] }, 'left'), _jsx(Stack, { direction, width: isSmallView ? '100%' : '25%', justifyContent, children: _jsx(Stack, { id: 'legend', children: MapLegend }, 'nwcmLeg') }, 'right')] }, 'controls'));
    return (_jsxs(Stack, { gap: 1, children: [contextArea && (_jsx(ObjectDetailCard, { mostProximateCustomer: undefined,
                matchedPrimary: contextArea,
                transactionFunctions,
                userFunctions,
                viewDefinition,
                //editMode: ComponentMode.EDIT,
                //deletable: true,
                allowedActions })), contextArea && contextArea.points?.length === 0 && _jsxs(Alert, { severity: "info", children: [contextArea.name, " does not have a map area"] }), _jsx(Grid, { container: true, children: _jsxs(Grid, { size: 12, sx: { minHeight: isSmallView ? '40vh' : '72cqh' }, children: [PortalDiv, controlsStack, map.current, !map.current && _jsx(Skeleton, { animation: 'wave', variant: 'rectangular', height: '100%' })] }) }), mapActionsDialog] }));
};
